import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

import { API } from "@/config";

function CheckingStatus() {
  return (
    <div data-testid="payment-checking">
      <Loader2 size={48} className="text-[#E8922F] mx-auto mb-4 animate-spin" />
      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Processing Payment...
      </h2>
      <p className="text-[#4A4A4A] text-sm">Please wait while we confirm your donation.</p>
    </div>
  );
}

function SuccessStatus({ paymentData }) {
  const amount = paymentData?.amount_total ? (paymentData.amount_total / 100).toFixed(2) : "";
  return (
    <div data-testid="payment-success">
      <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Thank You!
      </h2>
      <p className="text-[#4A4A4A] text-sm mb-4">
        Your generous donation of ${amount} has been received. God bless you!
      </p>
      <Link to="/" data-testid="return-home-btn">
        <Button className="btn-primary">Return Home</Button>
      </Link>
    </div>
  );
}

const ERROR_MESSAGES = {
  expired: "Your payment session has expired. Please try again.",
  timeout: "We couldn't confirm your payment. Please check your email for confirmation.",
  error: "There was an error checking your payment status.",
  "no-session": "No payment session found.",
};

function ErrorStatus({ status }) {
  return (
    <div data-testid="payment-error">
      <AlertCircle size={48} className="text-[#C4392D] mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {status === "expired" ? "Session Expired" : "Something Went Wrong"}
      </h2>
      <p className="text-[#4A4A4A] text-sm mb-4">{ERROR_MESSAGES[status]}</p>
      <Link to="/donate" data-testid="try-again-btn">
        <Button className="btn-primary">Try Again</Button>
      </Link>
    </div>
  );
}

export default function DonateSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const pollingRef = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("no-session"); return; }
    if (pollingRef.current) return;
    pollingRef.current = true;

    const poll = async (attempts) => {
      if (attempts >= 5) { setStatus("timeout"); return; }
      try {
        const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
        setPaymentData(data);
        if (data.payment_status === "paid") { setStatus("success"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch (err) {
        console.error("Payment status poll failed:", err.message);
        if (attempts < 4) setTimeout(() => poll(attempts + 1), 2000);
        else setStatus("error");
      }
    };
    poll(0);
  }, [sessionId]);

  const isError = ["error", "expired", "timeout", "no-session"].includes(status);

  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-[#FAFAFA] section-padding" data-testid="donate-success-page">
      <div className="bg-white border border-gray-100 rounded-2xl p-8 md:p-12 shadow-sm text-center max-w-md mx-4">
        {status === "checking" && <CheckingStatus />}
        {status === "success" && <SuccessStatus paymentData={paymentData} />}
        {isError && <ErrorStatus status={status} />}
      </div>
    </div>
  );
}
